import React, { useState } from "react"
import {
  type AutoSendSettingPatch,
  useDunningAutoSendSetting,
} from "../hooks/useDunningAutoSendSetting"
import type { DunningAutoSendSetting } from "../types"
import { formatDateTime } from "./shared"

/**
 * AIドラフト自動送付の設定カード。
 * 有効/無効トグル（ON時は確認ダイアログ）と対象条件パラメータの編集を提供する。
 * 実際の送付はスケジュールジョブが行う（この画面は設定のみ）。
 */

const StatusBadge: React.FC<{ enabled: boolean }> = ({ enabled }) =>
  enabled ? (
    <span className="rounded-full bg-green-100 px-2.5 py-0.5 text-[1.1rem] font-semibold text-green-700">
      有効
    </span>
  ) : (
    <span className="rounded-full bg-gray-200 px-2.5 py-0.5 text-[1.1rem] font-semibold text-black">
      無効
    </span>
  )

/** 数値input（0以上の整数のみ許可。それ以外は null） */
const parseIntField = (value: string): number | null =>
  /^\d+$/.test(value.trim()) ? Number.parseInt(value.trim(), 10) : null

const NumberField: React.FC<{
  label?: string
  value: string
  onChange: (value: string) => void
  widthClass?: string
}> = ({ label, value, onChange, widthClass = "w-[90px]" }) => (
  <input
    type="number"
    min={0}
    inputMode="numeric"
    aria-label={label}
    className={`${widthClass} rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-[1.25rem] tabular-nums text-black focus:border-primary focus:outline-none`}
    value={value}
    onChange={(event) => onChange(event.target.value)}
  />
)

/** 条件パラメータの編集フォーム。変更したキーだけを PATCH で送る */
const ConditionParamsForm: React.FC<{
  setting: DunningAutoSendSetting
  isSubmitting: boolean
  onSave: (patch: AutoSendSettingPatch) => Promise<boolean>
}> = ({ setting, isSubmitting, onSave }) => {
  const [minOverdueDays, setMinOverdueDays] = useState(String(setting.minOverdueDays))
  const [maxOverdueDays, setMaxOverdueDays] = useState(String(setting.maxOverdueDays))
  const [minDaysSinceLastContact, setMinDaysSinceLastContact] = useState(
    String(setting.minDaysSinceLastContact)
  )
  const [perRunCaseLimit, setPerRunCaseLimit] = useState(String(setting.perRunCaseLimit))

  const fields = [
    { key: "minOverdueDays" as const, input: minOverdueDays, current: setting.minOverdueDays },
    { key: "maxOverdueDays" as const, input: maxOverdueDays, current: setting.maxOverdueDays },
    {
      key: "minDaysSinceLastContact" as const,
      input: minDaysSinceLastContact,
      current: setting.minDaysSinceLastContact,
    },
    { key: "perRunCaseLimit" as const, input: perRunCaseLimit, current: setting.perRunCaseLimit },
  ]

  const hasInvalidInput = fields.some((field) => parseIntField(field.input) == null)
  // 変更したキーだけを部分更新として送る
  const patch: AutoSendSettingPatch = {}
  for (const field of fields) {
    const parsed = parseIntField(field.input)
    if (parsed != null && parsed !== field.current) patch[field.key] = parsed
  }
  const hasChanges = Object.keys(patch).length > 0
  const canSave = !hasInvalidInput && hasChanges && !isSubmitting

  const handleSave = () => {
    void onSave(patch)
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
      <span className="text-[1.25rem] font-semibold text-black">対象条件</span>
      <div className="mt-2.5 flex flex-wrap items-end gap-x-6 gap-y-3">
        <label className="flex flex-col gap-1">
          <span className="text-[1.15rem] font-medium text-black">遅延日数レンジ</span>
          <div className="flex items-center gap-2">
            <NumberField
              label="遅延日数の下限"
              value={minOverdueDays}
              onChange={setMinOverdueDays}
            />
            <span className="text-[1.25rem] text-black">〜</span>
            <NumberField
              label="遅延日数の上限"
              value={maxOverdueDays}
              onChange={setMaxOverdueDays}
            />
            <span className="text-[1.2rem] text-black">日</span>
          </div>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[1.15rem] font-medium text-black">最終接触からの日数</span>
          <div className="flex items-center gap-2">
            <NumberField
              label="最終接触からの日数"
              value={minDaysSinceLastContact}
              onChange={setMinDaysSinceLastContact}
            />
            <span className="text-[1.2rem] text-black">日以上</span>
          </div>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[1.15rem] font-medium text-black">1回の上限件数</span>
          <div className="flex items-center gap-2">
            <NumberField
              label="1回の上限件数"
              value={perRunCaseLimit}
              onChange={setPerRunCaseLimit}
            />
            <span className="text-[1.2rem] text-black">件</span>
          </div>
        </label>
        <button
          type="button"
          disabled={!canSave}
          onClick={handleSave}
          className="rounded-lg bg-primary px-5 py-2 text-[1.25rem] font-medium text-white hover:opacity-90 disabled:opacity-50"
        >
          {isSubmitting ? "保存中…" : "保存"}
        </button>
      </div>
      {hasInvalidInput && (
        <p className="mt-2 text-[1.15rem] text-amber-600">
          各項目には0以上の整数を入力してください。
        </p>
      )}
    </div>
  )
}

export const AutoSendSettingCard: React.FC = () => {
  const { setting, isLoading, error, updateSetting, isSubmitting, submitError, clearSubmitError } =
    useDunningAutoSendSetting()

  // 有効化の確認ダイアログ（OFF→ONの時だけ挟む。OFFにするのは即時）
  const [isConfirmingEnable, setIsConfirmingEnable] = useState(false)

  const handleSave = async (patch: AutoSendSettingPatch): Promise<boolean> => {
    const updated = await updateSetting(patch)
    return updated != null
  }

  const handleToggle = () => {
    if (!setting) return
    if (setting.enabled) {
      void handleSave({ enabled: false })
    } else {
      setIsConfirmingEnable(true)
    }
  }

  const handleConfirmEnable = async () => {
    const ok = await handleSave({ enabled: true })
    if (ok) setIsConfirmingEnable(false)
  }

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="flex flex-wrap items-center gap-3 border-b border-gray-200 bg-gray-50 px-5 py-4">
        <h2 className="text-[1.5rem] font-bold text-black">自動送付設定</h2>
        {setting && (
          <>
            <StatusBadge enabled={setting.enabled} />
            <span className="text-[1.2rem] tabular-nums text-black">
              最終更新: {formatDateTime(setting.updatedAt)}
            </span>
          </>
        )}
      </div>

      <div className="space-y-4 px-5 py-4">
        <p className="text-[1.2rem] text-black">
          条件を満たす督促ケースに対して、人間の確認なしでAIドラフトを自動送付します。
        </p>

        {error != null && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-[1.3rem] text-red-700">
            自動送付設定の取得に失敗しました。API が利用可能か確認してください。
          </div>
        )}

        {submitError && (
          <div className="flex items-start justify-between gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-[1.3rem] text-red-700">
            <span>{submitError}</span>
            <button
              type="button"
              onClick={clearSubmitError}
              className="shrink-0 text-[1.2rem] text-red-500 underline hover:text-red-700"
            >
              閉じる
            </button>
          </div>
        )}

        {isLoading && <p className="text-[1.3rem] text-black">読み込み中…</p>}

        {setting && (
          <>
            {/* 有効/無効トグル */}
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                role="switch"
                aria-checked={setting.enabled}
                aria-label="自動送付を有効にする"
                disabled={isSubmitting}
                onClick={handleToggle}
                className={`relative h-[26px] w-[48px] rounded-full transition-colors disabled:opacity-50 ${
                  setting.enabled ? "bg-green-500" : "bg-gray-300"
                }`}
              >
                <span
                  className={`absolute top-[3px] h-[20px] w-[20px] rounded-full bg-white shadow transition-all ${
                    setting.enabled ? "left-[25px]" : "left-[3px]"
                  }`}
                />
              </button>
              <span className="text-[1.3rem] font-medium text-black">
                {setting.enabled ? "自動送付は有効です" : "自動送付は無効です"}
              </span>
              <span className="text-[1.2rem] text-black">
                {setting.enabled
                  ? "条件を満たすケースへAIドラフトが自動送付されます"
                  : "ONにすると確認ダイアログが表示されます"}
              </span>
            </div>

            <ConditionParamsForm
              setting={setting}
              isSubmitting={isSubmitting}
              onSave={handleSave}
            />

            {/* 判定条件の説明 */}
            <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
              <p className="text-[1.2rem] leading-relaxed text-black">
                対象:
                オープン（Hold除外）・遅延日数が範囲内・有効な約束なし・最終接触から指定日数以上・
                <strong className="font-bold text-black">要返信のケースは対象外</strong>
                （ユーザー返信への対応は人間が行います）
              </p>
              <p className="mt-1.5 text-[1.15rem] text-black">
                送信ゲート（8:00〜21:00・1日3回上限・Hold・年末年始）は自動送付にもすべて適用されます。
              </p>
            </div>

            <p className="text-[1.15rem] text-black">
              自動送付はスケジュールジョブとして実行されます（実行スケジュールは現在未設定です）。この画面の設定は次回のジョブ実行から適用されます。
            </p>
          </>
        )}
      </div>

      {/* 有効化の確認ダイアログ */}
      {isConfirmingEnable && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-[480px] rounded-xl bg-white p-6 shadow-xl">
            <h3 className="text-[1.5rem] font-bold text-black">自動送付を有効にしますか？</h3>
            <p className="mt-2 text-[1.3rem] leading-relaxed text-black">
              人間の確認なしでAIドラフトが送信されます。全ての送信ゲート（8:00〜21:00・1日3回上限・Hold・年末年始）は適用されます。有効にしますか？
            </p>
            <div className="mt-5 flex justify-end gap-3">
              <button
                type="button"
                disabled={isSubmitting}
                onClick={() => setIsConfirmingEnable(false)}
                className="rounded-xl border border-gray-300 bg-white px-5 py-2.5 text-[1.3rem] font-medium text-black hover:bg-gray-50 disabled:opacity-50"
              >
                キャンセル
              </button>
              <button
                type="button"
                disabled={isSubmitting}
                onClick={() => void handleConfirmEnable()}
                className="rounded-xl bg-primary px-5 py-2.5 text-[1.3rem] font-medium text-white hover:opacity-90 disabled:opacity-50"
              >
                {isSubmitting ? "有効化中…" : "有効にする"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
